import React, { useState, useEffect } from "react";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";

/* ─── Inline styles ─────────────────────────────────────────────────────── */
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,400&display=swap');

  :root {
    --bg: #f0f2f7;
    --surface: #ffffff;
    --surface2: #f8f9fc;
    --border: #e2e6f0;
    --accent: #4f67ff;
    --accent-light: #eef0ff;
    --accent-dark: #3a4fd4;
    --success: #22c55e;
    --success-light: #dcfce7;
    --danger: #ef4444;
    --danger-light: #fee2e2;
    --warning: #f59e0b;
    --warning-light: #fef3c7;
    --muted: #8591a8;
    --text: #1a2035;
    --text-soft: #4b5673;
    --shadow-sm: 0 1px 3px rgba(0,0,0,.06), 0 1px 2px rgba(0,0,0,.04);
    --shadow: 0 4px 16px rgba(79,103,255,.08), 0 1px 4px rgba(0,0,0,.06);
    --radius: 14px;
    --radius-sm: 8px;
    --transition: .2s cubic-bezier(.4,0,.2,1);
  }

  .sp-wrap * { font-family: 'DM Sans', sans-serif; box-sizing: border-box; }
  .sp-navbar-sticky { position: sticky; top: 0; z-index: 1000; box-shadow: 0 2px 12px rgba(0,0,0,.08); }
  .sp-wrap { min-height: 100vh; background: var(--bg); padding: 28px 32px 48px; }

  /* ── Page Header ── */
  .sp-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 28px; flex-wrap: wrap; gap: 12px; }
  .sp-header-left { display: flex; align-items: center; gap: 14px; }
  .sp-icon-box { width: 48px; height: 48px; background: var(--accent); border-radius: var(--radius-sm); display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 12px rgba(79,103,255,.35); }
  .sp-icon-box svg { color: #fff; width: 22px; height: 22px; }
  .sp-title { font-family: 'Sora', sans-serif; font-size: 1.45rem; font-weight: 700; color: var(--text); line-height: 1.2; }
  .sp-subtitle { font-size: .82rem; color: var(--muted); margin-top: 2px; }

  .sp-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  .sp-btn { display: inline-flex; align-items: center; gap: 7px; padding: 9px 18px; border-radius: var(--radius-sm); font-size: .85rem; font-weight: 500; cursor: pointer; border: none; transition: all var(--transition); white-space: nowrap; }
  .sp-btn svg { width: 15px; height: 15px; flex-shrink: 0; }
  .sp-btn-primary { background: var(--accent); color: #fff; box-shadow: 0 2px 8px rgba(79,103,255,.4); }
  .sp-btn-primary:hover { background: var(--accent-dark); box-shadow: 0 4px 14px rgba(79,103,255,.5); transform: translateY(-1px); }
  .sp-btn-ghost { background: var(--surface); color: var(--text-soft); border: 1px solid var(--border); box-shadow: var(--shadow-sm); }
  .sp-btn-ghost:hover { background: var(--surface2); color: var(--text); border-color: #c8cedf; }
  .sp-btn-danger { background: var(--danger-light); color: var(--danger); border: 1px solid rgba(239,68,68,.2); }
  .sp-btn-danger:hover { background: var(--danger); color: #fff; }

  /* ── Card ── */
  .sp-card { background: var(--surface); border-radius: var(--radius); border: 1px solid var(--border); box-shadow: var(--shadow); overflow: hidden; animation: sp-fade-in .35s ease both; margin-bottom: 20px; }
  @keyframes sp-fade-in { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:none; } }
  .sp-card-header { display: flex; align-items: center; gap: 10px; padding: 16px 22px; background: var(--surface2); border-bottom: 1px solid var(--border); }
  .sp-card-header-icon { width: 32px; height: 32px; border-radius: 8px; background: var(--accent-light); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .sp-card-header-icon svg { width: 15px; height: 15px; color: var(--accent); }
  .sp-card-header-title { font-family: 'Sora', sans-serif; font-size: .92rem; font-weight: 600; color: var(--text); }
  .sp-card-header-desc { font-size: .78rem; color: var(--muted); margin-left: auto; }
  .sp-card-body { padding: 24px 22px; }

  /* ── Grid ── */
  .sp-grid { display: grid; gap: 20px; }
  .sp-grid-2 { grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); }

  /* ── Form Fields ── */
  .sp-field { display: flex; flex-direction: column; gap: 6px; }
  .sp-label { font-size: .78rem; font-weight: 600; color: var(--text-soft); text-transform: uppercase; letter-spacing: .05em; }
  .sp-label-hint { font-size: .72rem; color: var(--muted); margin-left: 6px; font-weight: 400; text-transform: none; letter-spacing: 0; }
  .sp-input, .sp-select { width: 100%; padding: 10px 13px; border: 1.5px solid var(--border); border-radius: var(--radius-sm); font-size: .88rem; font-family: 'DM Sans', sans-serif; color: var(--text); background: var(--surface); transition: all var(--transition); outline: none; }
  .sp-input:focus, .sp-select:focus { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(79,103,255,.12); }
  .sp-input:read-only { background: var(--surface2); color: var(--text-soft); cursor: default; }

  /* ── Input with icon ── */
  .sp-input-wrap { position: relative; }
  .sp-input-wrap .sp-input { padding-left: 38px; }
  .sp-input-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); width: 15px; height: 15px; color: var(--muted); pointer-events: none; transition: color var(--transition); }
  .sp-input-wrap:focus-within .sp-input-icon { color: var(--accent); }

  /* ── Token field ── */
  .sp-token-wrap { position: relative; }
  .sp-token-wrap .sp-input { padding-right: 80px; font-family: 'Courier New', monospace; font-size: .82rem; letter-spacing: .03em; }
  .sp-token-toggle { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); background: var(--accent-light); color: var(--accent); border: none; border-radius: 6px; padding: 4px 8px; font-size: .72rem; font-weight: 600; cursor: pointer; font-family: 'DM Sans', sans-serif; transition: all var(--transition); }
  .sp-token-toggle:hover { background: var(--accent); color: #fff; }

  /* ── Select with loader ── */
  .sp-select-wrap { position: relative; }
  .sp-select-wrap .sp-select { appearance: none; padding-right: 36px; }
  .sp-select-arrow { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); pointer-events: none; width: 14px; height: 14px; color: var(--muted); }
  .sp-select-loading { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); width: 14px; height: 14px; border: 2px solid var(--border); border-top-color: var(--accent); border-radius: 50%; animation: sp-spin .7s linear infinite; }
  @keyframes sp-spin { to { transform: translateY(-50%) rotate(360deg); } }

  /* ── Info bar ── */
  .sp-info-bar { display: flex; align-items: center; gap: 10px; padding: 10px 14px; border-radius: var(--radius-sm); background: var(--accent-light); border: 1.5px solid rgba(79,103,255,.2); margin-top: 8px; }
  .sp-info-bar svg { width: 14px; height: 14px; color: var(--accent); flex-shrink: 0; }
  .sp-info-bar-text { font-size: .78rem; color: var(--accent); font-weight: 500; }
  .sp-info-bar-text span { font-weight: 700; }

  /* ── Edit mode banner ── */
  .sp-edit-banner { display: flex; align-items: center; gap: 10px; padding: 10px 14px; border-radius: var(--radius-sm); background: var(--warning-light); border: 1.5px solid rgba(245,158,11,.25); margin-bottom: 16px; }
  .sp-edit-banner svg { width: 15px; height: 15px; color: var(--warning); flex-shrink: 0; }
  .sp-edit-banner-text { font-size: .8rem; color: #92400e; font-weight: 500; flex: 1; }
  .sp-edit-banner-cancel { font-size: .75rem; font-weight: 600; color: var(--danger); cursor: pointer; background: none; border: none; padding: 0; font-family: 'DM Sans', sans-serif; }
  .sp-edit-banner-cancel:hover { text-decoration: underline; }

  /* ── Employee chips ── */
  .sp-employee-list { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; min-height: 36px; }
  .sp-emp-chip { display: inline-flex; align-items: center; gap: 6px; padding: 5px 12px 5px 6px; background: var(--accent-light); color: var(--accent); border-radius: 100px; font-size: .78rem; font-weight: 500; border: 1.5px solid rgba(79,103,255,.2); cursor: pointer; transition: all var(--transition); user-select: none; }
  .sp-emp-chip.selected { background: var(--accent); color: #fff; border-color: var(--accent-dark); box-shadow: 0 2px 8px rgba(79,103,255,.35); }
  .sp-emp-chip:hover:not(.selected) { background: rgba(79,103,255,.15); }
  .sp-emp-avatar { width: 22px; height: 22px; border-radius: 50%; background: rgba(255,255,255,.35); display: flex; align-items: center; justify-content: center; font-size: .68rem; font-weight: 700; flex-shrink: 0; }

  /* ── Empty state ── */
  .sp-empty { display: flex; flex-direction: column; align-items: center; gap: 6px; padding: 20px; color: var(--muted); font-size: .82rem; text-align: center; }
  .sp-empty svg { width: 28px; height: 28px; opacity: .4; }

  /* ── Divider ── */
  .sp-divider { border: none; border-top: 1px solid var(--border); margin: 20px 0; }

  /* ── File / utility buttons ── */
  .sp-file-btn { display: inline-flex; align-items: center; gap: 6px; padding: 5px 14px; border-radius: var(--radius-sm); font-size: .78rem; font-weight: 600; cursor: pointer; border: 1.5px solid var(--accent); background: var(--accent-light); color: var(--accent); font-family: 'DM Sans', sans-serif; transition: all var(--transition); }
  .sp-file-btn:hover { background: var(--accent); color: #fff; }

  /* ── Configs table ── */
  .sp-table-wrap { overflow-x: auto; }
  .sp-table { width: 100%; border-collapse: collapse; font-size: .83rem; }
  .sp-table th { padding: 10px 14px; text-align: left; font-size: .72rem; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: var(--muted); background: var(--surface2); border-bottom: 1px solid var(--border); white-space: nowrap; }
  .sp-table td { padding: 12px 14px; border-bottom: 1px solid var(--border); color: var(--text-soft); vertical-align: middle; }
  .sp-table tr:last-child td { border-bottom: none; }
  .sp-table tr:hover td { background: var(--surface2); }
  .sp-table td.mono { font-family: 'Courier New', monospace; font-size: .75rem; color: var(--muted); max-width: 180px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .sp-badge { display: inline-flex; align-items: center; padding: 2px 10px; border-radius: 100px; font-size: .72rem; font-weight: 600; }
  .sp-badge-blue { background: var(--accent-light); color: var(--accent); }
  .sp-row-actions { display: flex; align-items: center; gap: 6px; }
  .sp-icon-btn { display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 30px; border-radius: 7px; border: 1px solid var(--border); background: var(--surface); cursor: pointer; transition: all var(--transition); color: var(--text-soft); }
  .sp-icon-btn:hover.view { background: var(--accent-light); color: var(--accent); border-color: rgba(79,103,255,.3); }
  .sp-icon-btn:hover.edit { background: var(--warning-light); color: var(--warning); border-color: rgba(245,158,11,.3); }
  .sp-icon-btn:hover.del { background: var(--danger-light); color: var(--danger); border-color: rgba(239,68,68,.3); }
  .sp-icon-btn svg { width: 13px; height: 13px; }

  /* ── View Modal ── */
  .sp-modal-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,.35); backdrop-filter: blur(4px); z-index: 2000; display: flex; align-items: center; justify-content: center; padding: 20px; animation: sp-fade-in .2s ease; }
  .sp-modal { background: var(--surface); border-radius: var(--radius); border: 1px solid var(--border); box-shadow: 0 20px 60px rgba(0,0,0,.18); width: 100%; max-width: 560px; overflow: hidden; }
  .sp-modal-header { display: flex; align-items: center; justify-content: space-between; padding: 16px 22px; background: var(--surface2); border-bottom: 1px solid var(--border); }
  .sp-modal-title { font-family: 'Sora', sans-serif; font-size: .95rem; font-weight: 700; color: var(--text); }
  .sp-modal-close { width: 28px; height: 28px; border-radius: 7px; border: 1px solid var(--border); background: var(--surface); cursor: pointer; display: flex; align-items: center; justify-content: center; color: var(--muted); transition: all var(--transition); }
  .sp-modal-close:hover { background: var(--danger-light); color: var(--danger); border-color: rgba(239,68,68,.2); }
  .sp-modal-close svg { width: 13px; height: 13px; }
  .sp-modal-body { padding: 22px; }
  .sp-detail-row { display: flex; flex-direction: column; gap: 3px; margin-bottom: 16px; }
  .sp-detail-row:last-child { margin-bottom: 0; }
  .sp-detail-label { font-size: .72rem; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: var(--muted); }
  .sp-detail-val { font-size: .88rem; color: var(--text); word-break: break-all; }
  .sp-detail-val.mono { font-family: 'Courier New', monospace; font-size: .78rem; color: var(--text-soft); }
  .sp-chip-list { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 4px; }
  .sp-chip-sm { display: inline-flex; align-items: center; padding: 3px 10px; border-radius: 100px; font-size: .72rem; font-weight: 600; background: var(--accent-light); color: var(--accent); border: 1px solid rgba(79,103,255,.2); }

  /* ── Loading skeleton ── */
  .sp-skeleton { background: linear-gradient(90deg, var(--surface2) 25%, var(--border) 50%, var(--surface2) 75%); background-size: 200% 100%; animation: sp-shimmer 1.2s infinite; border-radius: 6px; height: 14px; }
  @keyframes sp-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

  @media (max-width: 640px) {
    .sp-wrap { padding: 16px; }
    .sp-header { flex-direction: column; align-items: flex-start; }
  }
`;

/* ─── Icons ─────────────────────────────────────────────────────────────── */
const Icon = {
  Link: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>,
  Save: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></svg>,
  Back: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>,
  Folder: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /></svg>,
  Users: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>,
  ChevronDown: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>,
  Eye: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>,
  Edit: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>,
  Trash: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>,
  X: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>,
  Plus: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>,
  Alert: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>,
  List: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>,
};

/* ─── Helpers ────────────────────────────────────────────────────────────── */
const empName = (emp) => {
  if (emp.firstName || emp.lastName) return `${emp.firstName || ""} ${emp.lastName || ""}`.trim();
  return emp.name || emp.fullName || emp.username || `#${emp.id}`;
};
const initials = (name = "") => name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2) || "?";
const maskToken = (tok = "") => tok.length <= 8 ? "••••••••" : tok.slice(0, 6) + "••••••••" + tok.slice(-4);

/* ─── View Modal ─────────────────────────────────────────────────────────── */
const ViewModal = ({ config, projects, token, apiBaseUrl, onClose }) => {

  // ✅ All hooks FIRST — before any early return
  const [empMap, setEmpMap] = useState({});
  const [empLoading, setEmpLoading] = useState(false);

  useEffect(() => {
    if (!config?.projectId) return;
    setEmpLoading(true);
    fetch(`${apiBaseUrl}/api/manageteam/project/${config.projectId}/employees`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data)) {
          const map = {};
          data.forEach(emp => { map[String(emp.id)] = empName(emp); });
          setEmpMap(map);
        }
      })
      .catch(() => { })
      .finally(() => setEmpLoading(false));
  }, [config?.projectId]);

  // ✅ Early return AFTER hooks
  if (!config) return null;

  const project = projects.find(p => String(p.id) === String(config.projectId));
  const projectLabel = project
    ? (project.projectName || project.name)
    : config.projectId ? `Project #${config.projectId}` : "—";
  const empIds = config.employeeIds ? config.employeeIds.split(",").filter(Boolean) : [];

  return (
    <div className="sp-modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="sp-modal">
        <div className="sp-modal-header">
          <span className="sp-modal-title">{projectLabel} — Integration</span>
          <button className="sp-modal-close" onClick={onClose}><Icon.X /></button>
        </div>
        <div className="sp-modal-body">
          <div className="sp-detail-row">
            <span className="sp-detail-label">API Base URL</span>
            <span className="sp-detail-val mono">{config.apiBaseUrl || "—"}</span>
          </div>
          <div className="sp-detail-row">
            <span className="sp-detail-label">Access Token</span>
            <span className="sp-detail-val mono">{maskToken(config.accessToken)}</span>
          </div>
          <div className="sp-detail-row">
            <span className="sp-detail-label">Project</span>
            <span className="sp-detail-val">{projectLabel}</span>
          </div>
          <div className="sp-detail-row">
            <span className="sp-detail-label">Employees ({empIds.length})</span>
            <div className="sp-chip-list">
              {empIds.length === 0 && (
                <span style={{ fontSize: ".78rem", color: "var(--muted)" }}>None</span>
              )}
              {empIds.map(id => (
                <span key={id} className="sp-chip-sm">
                  {empLoading ? `ID: ${id}` : (empMap[id] || `ID: ${id}`)}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Component ─────────────────────────────────────────────────────────── */
const AdminIntegrationConfig = () => {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  /* ── Form visibility ── */
  const [showForm, setShowForm] = useState(false); // hidden by default

  /* ── Form state ── */
  const [editingId, setEditingId] = useState(null);
  const [integration, setIntegration] = useState({ apiBaseUrl: "", accessToken: "" });
  const [showToken, setShowToken] = useState(false);

  /* ── Project / employee state ── */
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [pendingEmpIds, setPendingEmpIds] = useState(null);

  /* ── Saved configs list ── */
  const [configs, setConfigs] = useState([]);
  const [configsLoading, setConfigsLoading] = useState(false);
  const [viewConfig, setViewConfig] = useState(null);

  /* ── Auth guard ── */
  useEffect(() => {
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
    if (!roles.includes("ROLE_ADMIN")) { toast.error("Not authorized."); navigate("/"); return; }
  }, [navigate]);

  /* ── Fetch all saved configs ── */
  const fetchConfigs = () => {
    setConfigsLoading(true);
    fetch(`${API_BASE_URL}/api/teamwork/admin`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => setConfigs(Array.isArray(data) ? data : []))
      .catch(() => setConfigs([]))
      .finally(() => setConfigsLoading(false));
  };
  useEffect(() => { fetchConfigs(); }, []);

  /* ── Fetch projects ── */
  useEffect(() => {
    setProjectsLoading(true);
    fetch(`${API_BASE_URL}/api/projects/all`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => setProjects(Array.isArray(data) ? data : []))
      .catch(() => setProjects([]))
      .finally(() => setProjectsLoading(false));
  }, []);

  /* ── Fetch employees on project change ── */
  useEffect(() => {
    if (!selectedProject) { setEmployees([]); setSelectedEmployees([]); return; }
    setEmployeesLoading(true);
    setSelectedEmployees([]);
    fetch(`${API_BASE_URL}/api/manageteam/project/${selectedProject}/employees`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        setEmployees(list);
        setSelectedEmployees(list.map(emp => String(emp.id)));
      })
      .catch(() => setEmployees([]))
      .finally(() => setEmployeesLoading(false));
  }, [selectedProject]);

  /* ── Restore pending employee selection after employees load ── */
  useEffect(() => {
    if (!employeesLoading && pendingEmpIds !== null) {
      setSelectedEmployees(pendingEmpIds);
      setPendingEmpIds(null);
    }
  }, [employeesLoading]);

  /* ── Cancel / reset form ── */
  const handleCancelEdit = () => {
    setEditingId(null);
    setPendingEmpIds(null);
    setIntegration({ apiBaseUrl: "", accessToken: "" });
    setSelectedProject("");
    setSelectedEmployees([]);
    setShowForm(false); // ✅ hide form on cancel
  };

  /* ── Load config into form for editing ── */
  const handleEditWithPending = (cfg) => {
    const savedIds = cfg.employeeIds ? cfg.employeeIds.split(",").filter(Boolean) : [];
    setPendingEmpIds(savedIds);
    setEditingId(cfg.id);
    setIntegration({ apiBaseUrl: cfg.apiBaseUrl || "", accessToken: cfg.accessToken || "" });
    setSelectedProject(cfg.projectId ? String(cfg.projectId) : "");
    setShowForm(true); // ✅ show form when editing
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* ── Save / Update ── */
  const handleSave = async () => {
    if (!integration.apiBaseUrl || !integration.accessToken) {
      toast.error("API URL and Token are required"); return;
    }
    if (!selectedProject) { toast.error("Please select a project"); return; }
    if (!selectedEmployees.length) { toast.error("Please select at least one employee"); return; }

    const isEdit = editingId !== null;
    const url = isEdit
      ? `${API_BASE_URL}/api/teamwork/admin/${editingId}`
      : `${API_BASE_URL}/api/teamwork/admin`;

    try {
      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          apiBaseUrl: integration.apiBaseUrl,
          accessToken: integration.accessToken,
          projectId: selectedProject,
          employeeIds: selectedEmployees,
        }),
      });
      if (!res.ok) throw new Error("Failed to save");
      toast.success(isEdit ? "Integration updated successfully" : "Integration saved successfully");
      handleCancelEdit();
      fetchConfigs();
    } catch (err) {
      console.error(err);
      toast.error("Error saving integration");
    }
  };

  /* ── Delete ── */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this integration config?")) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/teamwork/admin/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      toast.success("Config deleted");
      fetchConfigs();
      if (editingId === id) handleCancelEdit();
    } catch {
      toast.error("Error deleting config");
    }
  };

  const activeProject = projects.find(p => String(p.id) === String(selectedProject));

  return (
    <>
      <style>{css}</style>
      <div className="sp-navbar-sticky"><AdminNavbar /></div>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ✅ Pass token + apiBaseUrl to modal for employee name resolution */}
      {viewConfig && (
        <ViewModal
          config={viewConfig}
          projects={projects}
          token={token}
          apiBaseUrl={API_BASE_URL}
          onClose={() => setViewConfig(null)}
        />
      )}

      <div className="sp-wrap">

        {/* ── Header ── */}
        <div className="sp-header">
          <div className="sp-header-left">
            <div className="sp-icon-box"><Icon.Link /></div>
            <div>
              <div className="sp-title">TeamWork Integration Settings</div>
              <div className="sp-subtitle">Configure API connection &amp; project scope</div>
            </div>
          </div>
          <div className="sp-actions">
            {/* ✅ New button — only when form is hidden */}
            {!showForm && (
              <button className="sp-btn sp-btn-primary" onClick={() => setShowForm(true)}>
                <Icon.Plus /> New Integration
              </button>
            )}
            {/* ✅ Save/Update — only when form is visible */}
            {showForm && (
              <button className="sp-btn sp-btn-primary" onClick={handleSave}>
                <Icon.Save /> {editingId ? "Save Changes" : "Save "}
              </button>
            )}
            {showForm && (
              <button className="sp-btn sp-btn-ghost" onClick={handleCancelEdit}>
                <Icon.X /> Cancel
              </button>
            )}
            <button className="sp-btn sp-btn-ghost" onClick={() => navigate(-1)}>
              <Icon.Back /> Back
            </button>
          </div>
        </div>

        {/* ── Form Card — only shown when showForm is true ── */}
        {showForm && (
          <div className="sp-card">
            <div className="sp-card-header">
              <div className="sp-card-header-icon"><Icon.Link /></div>
              <span className="sp-card-header-title">
                {/* ✅ Show project name in form header when editing */}
                {editingId
                  ? `Editing — ${activeProject ? (activeProject.projectName || activeProject.name) : `Config #${editingId}`}`
                  : "New Integration"}
              </span>
              <span className="sp-card-header-desc">API connection &amp; project scope</span>
            </div>
            <div className="sp-card-body">

              {/* Edit mode banner */}
              {editingId && (
                <div className="sp-edit-banner">
                  <Icon.Alert />
                  <span className="sp-edit-banner-text">
                    You are editing an existing configuration. Changes will overwrite the saved values.
                  </span>
                  <button className="sp-edit-banner-cancel" onClick={handleCancelEdit}>
                    Cancel &amp; create new
                  </button>
                </div>
              )}

              {/* Row 1: API Base URL + Access Token */}
              <div className="sp-grid sp-grid-2" style={{ marginBottom: 20 }}>
                <div className="sp-field">
                  <label className="sp-label">
                    API Base URL
                    <span className="sp-label-hint">The root endpoint for all API calls</span>
                  </label>
                  <div className="sp-input-wrap">
                    <input
                      className="sp-input"
                      type="text"
                      placeholder="https://your-api.example.com"
                      value={integration.apiBaseUrl}
                      onChange={e => setIntegration({ ...integration, apiBaseUrl: e.target.value })}
                    />
                    <svg className="sp-input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                    </svg>
                  </div>
                </div>

                <div className="sp-field">
                  <label className="sp-label">
                    Access Token
                    <span className="sp-label-hint">Bearer token for authentication</span>
                  </label>
                  <div className="sp-token-wrap">
                    <input
                      className="sp-input"
                      type={showToken ? "text" : "password"}
                      placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6..."
                      value={integration.accessToken}
                      onChange={e => setIntegration({ ...integration, accessToken: e.target.value })}
                    />
                    <button type="button" className="sp-token-toggle" onClick={() => setShowToken(v => !v)}>
                      {showToken ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>
              </div>

              <hr className="sp-divider" />

              {/* Row 2: Select Project + Select Employee */}
              <div className="sp-grid sp-grid-2">
                <div className="sp-field">
                  <label className="sp-label">
                    Select Project
                    <span className="sp-label-hint">{projects.length} project{projects.length !== 1 ? "s" : ""} available</span>
                  </label>
                  <div className="sp-select-wrap">
                    <select
                      className="sp-select"
                      value={selectedProject}
                      onChange={e => setSelectedProject(e.target.value)}
                      disabled={projectsLoading}
                    >
                      <option value="">— Choose a project —</option>
                      {projects.map(p => {
                        const label = p.projectName || p.name || `Project #${p.id}`;
                        const display = label.length > 40 ? label.slice(0, 38) + "…" : label;
                        return (
                          <option key={p.id} value={p.id} title={label}>{display}</option>
                        );
                      })}
                    </select>
                    {projectsLoading ? <span className="sp-select-loading" /> : <span className="sp-select-arrow"><Icon.ChevronDown /></span>}
                  </div>
                  {activeProject && (
                    <div className="sp-info-bar">
                      <Icon.Folder />
                      <span className="sp-info-bar-text">Active: <span>{activeProject.projectName || activeProject.name}</span></span>
                    </div>
                  )}
                </div>

                <div className="sp-field">
                  <label className="sp-label">
                    Select Employee
                    <span className="sp-label-hint">
                      {selectedProject ? `${selectedEmployees.length} of ${employees.length} selected` : "Select a project first"}
                    </span>
                  </label>
                  {selectedProject && !employeesLoading && employees.length > 0 && (
                    <div style={{ display: "flex", gap: 8, marginBottom: 4 }}>
                      <button type="button" className="sp-file-btn" onClick={() => setSelectedEmployees(employees.map(e => String(e.id)))}>Select All</button>
                      <button type="button" className="sp-file-btn" style={{ background: "var(--surface2)", color: "var(--text-soft)", borderColor: "var(--border)" }} onClick={() => setSelectedEmployees([])}>Deselect All</button>
                    </div>
                  )}
                  {selectedProject && (
                    <div className="sp-employee-list">
                      {employeesLoading && <span style={{ fontSize: ".78rem", color: "var(--muted)" }}>Loading members…</span>}
                      {!employeesLoading && employees.length === 0 && (
                        <div className="sp-empty"><Icon.Users />No employees found for this project</div>
                      )}
                      {!employeesLoading && employees.map(emp => {
                        const name = empName(emp);
                        const isSelected = selectedEmployees.includes(String(emp.id));
                        return (
                          <div
                            key={emp.id}
                            className={`sp-emp-chip ${isSelected ? "selected" : ""}`}
                            onClick={() =>
                              setSelectedEmployees(prev =>
                                isSelected ? prev.filter(id => id !== String(emp.id)) : [...prev, String(emp.id)]
                              )
                            }
                            title={isSelected ? `Deselect ${name}` : `Select ${name}`}
                          >
                            <span className="sp-emp-avatar">{initials(name)}</span>
                            {name}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>
        )}

        {/* ── Saved Configs List ── */}
        <div className="sp-card">
          <div className="sp-card-header">
            <div className="sp-card-header-icon"><Icon.List /></div>
            <span className="sp-card-header-title">Saved Configurations</span>
            <span className="sp-card-header-desc">{configs.length} record{configs.length !== 1 ? "s" : ""}</span>
          </div>
          <div className="sp-card-body" style={{ padding: 0 }}>
            {configsLoading ? (
              <div style={{ padding: 22, display: "flex", flexDirection: "column", gap: 10 }}>
                {[1, 2, 3].map(i => <div key={i} className="sp-skeleton" style={{ width: i === 2 ? "60%" : "80%" }} />)}
              </div>
            ) : configs.length === 0 ? (
              <div className="sp-empty" style={{ padding: 32 }}>
                <Icon.List />No saved configurations yet. Click "New Integration" above to create one.
              </div>
            ) : (
              <div className="sp-table-wrap">
                <table className="sp-table">
                  <thead>
                    <tr>
                      <th>S.No</th>
                      <th>API Base URL</th>
                      <th>Token</th>
                      <th>Project</th>
                      <th>Employees</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {configs.map((cfg, index) => {
                      const empCount = cfg.employeeIds ? cfg.employeeIds.split(",").filter(Boolean).length : 0;
                      const proj = projects.find(p => String(p.id) === String(cfg.projectId));
                      return (
                        <tr key={cfg.id} style={editingId === cfg.id ? { background: "var(--warning-light)" } : {}}>
                          {/* ✅ Serial number */}
                          <td><span className="sp-badge sp-badge-blue">{index + 1}</span></td>
                          <td className="mono">{cfg.apiBaseUrl || "—"}</td>
                          <td className="mono">{maskToken(cfg.accessToken)}</td>
                          {/* ✅ Project name */}
                          <td>{proj ? (proj.projectName || proj.name) : (cfg.projectId || "—")}</td>
                          <td>
                            <span className="sp-badge sp-badge-blue">{empCount} emp{empCount !== 1 ? "s" : ""}</span>
                          </td>
                          <td>
                            <div className="sp-row-actions">
                              <button className="sp-icon-btn view" title="View" onClick={() => setViewConfig(cfg)}>
                                <Icon.Eye />
                              </button>
                              <button className="sp-icon-btn edit" title="Edit" onClick={() => handleEditWithPending(cfg)}>
                                <Icon.Edit />
                              </button>
                              <button className="sp-icon-btn del" title="Delete" onClick={() => handleDelete(cfg.id)}>
                                <Icon.Trash />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

      </div>
    </>
  );
};

export default AdminIntegrationConfig;